import { isOwfGrantIneligibleStore } from "@faire/deprecated-retailer-visitor-shared/features/OpenWithFaire/isOwfGrantIneligibleStore";
import { SignUpRequestExpanded } from "@faire/deprecated-retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { isRetailerOnNetTerms } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/isRetailerOnNetTerms";
import { isSupportedCountry } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingSupportedCountries";
import {
  hasMatchedBusiness,
  isRetailerEligibleForOwF,
  shouldShowOpeningMonthQuestion,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_utils/signupUtilities";
import { isOpeningSoon } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_utils/signupUtilities";
import { IStepper } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/signupStepper";
import { Step } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/Step";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";

export const signUpSteps: Step[] = [
  Step.PERSONAL_INFO,
  Step.BUSINESS_TYPE,
  Step.STORE_INFO,
  Step.STORE_TYPE_S1,
  Step.OPENING_YEAR,
  Step.OPENING_MONTH,
  Step.FAIRE_DIRECT_SURVEY,
  Step.ANNUAL_SALES,
  Step.OWF_GRANT_SOCIAL_MEDIA,
  Step.RESELLER_CERT,
  Step.SELECT_NET_TERMS,
  Step.SCC_AGREEMENT,
  Step.SCC_PERSONAL_INFO_FORM,
  Step.SCC_NET_TERMS,
  Step.OWF_UPSELL,
  Step.OWF_APPLICATION,
  Step.CONSUMER,
  Step.NOTIFY_ME,
  Step.OWF_SUCCESS,
];

export const endingSteps: Step[] = [
  Step.CONSUMER,
  Step.NOTIFY_ME_SUCCESS,
  Step.END,
  Step.OWF_APPLICATION,
  Step.OWF_UPSELL,
  Step.OWF_GRANT_SOCIAL_MEDIA,
  Step.SCC_NET_TERMS,
  Step.OWF_SUCCESS,
];

export const nonFinalSteps = signUpSteps.filter(
  (step) => !endingSteps.includes(step)
);

export const signUpStepper: IStepper = (currentStep, requestState, options) => {
  const nextStepper = steppers[currentStep];
  return (
    nextStepper?.(currentStep, requestState, options) ?? Step.PERSONAL_INFO
  );
};

const personalInfoStepper: IStepper = (_, requestState) => {
  return isSupportedCountry(requestState?.country ?? "")
    ? Step.BUSINESS_TYPE
    : Step.NOTIFY_ME;
};

const businessTypeStepper: IStepper = () => {
  return Step.STORE_INFO;
};

const storeInfoStepper: IStepper = (_, requestState) => {
  return returnStoreTypeStepOrOpeningYear(requestState);
};

const storeTypeS1Stepper: IStepper = () => {
  return Step.OPENING_YEAR;
};

const openingYearStepper: IStepper = (_, requestState) => {
  if (shouldShowOpeningMonthQuestion(requestState)) {
    return Step.OPENING_MONTH;
  }
  if (requestState?.isFD) {
    return Step.FAIRE_DIRECT_SURVEY;
  }
  return Step.ANNUAL_SALES;
};

const openingMonthStepper: IStepper = (_, requestState) => {
  if (requestState?.isFD) {
    return Step.FAIRE_DIRECT_SURVEY;
  }

  return returnAnnualSalesOrSkipIt(requestState?.reported_creation_time);
};

const surveyStepper: IStepper = (_, requestState) => {
  return returnAnnualSalesOrSkipIt(requestState?.reported_creation_time);
};

const annualSalesStepper: IStepper = () => {
  return Step.RESELLER_CERT;
};

const resellerCertStepper: IStepper = (_, requestState, options) => {
  const retailer = options?.retailer;
  const skipOwFUpsell = options?.skipOwFUpsell;
  return returnNetTermsOrSkipIt(
    retailer,
    requestState?.reported_creation_time,
    requestState?.isOwFGrantFlow,
    skipOwFUpsell
  );
};

const selectNetTermsStepper: IStepper = (_, requestState, options) => {
  if (options?.startScc) {
    return Step.SCC_AGREEMENT;
  }

  return returnOwFStepOrEnd(
    options?.retailer,
    requestState?.reported_creation_time,
    !!requestState?.isOwFGrantFlow,
    options?.skipOwFUpsell
  );
};

const owfGrantSocialMediaStepper: IStepper = () => {
  return Step.END;
};

const sccAgreementStepper: IStepper = (_, __, options) => {
  if (options?.skipScc) {
    return Step.END;
  }
  return Step.SCC_PERSONAL_INFO_FORM;
};

const sccPersonalInfoFormStepper: IStepper = (_, __, options) => {
  if (options?.skipScc) {
    return Step.END;
  }
  return Step.SCC_NET_TERMS;
};

const sccNetTermsFormStepper: IStepper = () => {
  return Step.END;
};

const owFUpsellStepper: IStepper = (_, __, options) => {
  return options?.skipOwF ? Step.END : Step.OWF_APPLICATION;
};

const owFApplicationStepper: IStepper = (_, __, options) => {
  if (!options?.realTimeOwF) {
    return Step.END;
  }
  // roychon-faire TODO: once backend logic is completed, add check for if retailer is auto-approved
  return Step.OWF_SUCCESS;
};

const owFSuccessStepper: IStepper = () => {
  return Step.END;
};

const steppers: Partial<Record<Step, IStepper>> = {
  [Step.PERSONAL_INFO]: personalInfoStepper,
  [Step.BUSINESS_TYPE]: businessTypeStepper,
  [Step.STORE_INFO]: storeInfoStepper,
  [Step.STORE_TYPE_S1]: storeTypeS1Stepper,
  [Step.OPENING_YEAR]: openingYearStepper,
  [Step.OPENING_MONTH]: openingMonthStepper,
  [Step.FAIRE_DIRECT_SURVEY]: surveyStepper,
  [Step.ANNUAL_SALES]: annualSalesStepper,
  [Step.OWF_GRANT_SOCIAL_MEDIA]: owfGrantSocialMediaStepper,
  [Step.RESELLER_CERT]: resellerCertStepper,
  [Step.SELECT_NET_TERMS]: selectNetTermsStepper,
  [Step.SCC_AGREEMENT]: sccAgreementStepper,
  [Step.SCC_PERSONAL_INFO_FORM]: sccPersonalInfoFormStepper,
  [Step.SCC_NET_TERMS]: sccNetTermsFormStepper,
  [Step.OWF_UPSELL]: owFUpsellStepper,
  [Step.OWF_APPLICATION]: owFApplicationStepper,
  [Step.OWF_SUCCESS]: owFSuccessStepper,
};

const returnAnnualSalesOrSkipIt = (
  reportedCreationTime: keyof typeof IRetailer.ReportedCreationTime | undefined
) => {
  if (isOpeningSoon(reportedCreationTime)) {
    return Step.RESELLER_CERT;
  }
  return Step.ANNUAL_SALES;
};

const returnNetTermsOrSkipIt = (
  retailer: IRetailer | undefined,
  reportedCreationTime: keyof typeof IRetailer.ReportedCreationTime | undefined,
  isOwFGrantFlow?: boolean,
  skipOwFUpsell?: boolean
) => {
  // skip SELECT_NET_TERMS and OWF_UPSELL in OwF grant flow
  if (isOwFGrantFlow) {
    return returnOwFGrantSocialMediaStepOrEnd(retailer, reportedCreationTime);
  }

  if (isRetailerOnNetTerms(retailer)) {
    return Step.SELECT_NET_TERMS;
  }
  return returnOwFStepOrEnd(
    retailer,
    reportedCreationTime,
    isOwFGrantFlow,
    skipOwFUpsell
  );
};

const returnOwFStepOrEnd = (
  retailer: IRetailer | undefined,
  reportedCreationTime: keyof typeof IRetailer.ReportedCreationTime | undefined,
  isOwFGrantFlow?: boolean,
  skipOwFUpsell?: boolean
) => {
  // skip SELECT_NET_TERMS and OWF_UPSELL in OwF grant flow
  if (isOwFGrantFlow) {
    return returnOwFGrantSocialMediaStepOrEnd(retailer, reportedCreationTime);
  }

  if (isRetailerEligibleForOwF(retailer)) {
    if (skipOwFUpsell) {
      return Step.OWF_APPLICATION;
    }
    return Step.OWF_UPSELL;
  }
  return Step.END;
};

const returnOwFGrantSocialMediaStepOrEnd = (
  retailer: IRetailer | undefined,
  reportedCreationTime: keyof typeof IRetailer.ReportedCreationTime | undefined
) => {
  if (
    isOwfGrantIneligibleStore(
      reportedCreationTime,
      isRetailerEligibleForOwF(retailer)
    )
  ) {
    return Step.END;
  }

  return Step.OWF_GRANT_SOCIAL_MEDIA;
};

const returnStoreTypeStepOrOpeningYear = (
  requestState: Partial<SignUpRequestExpanded> | undefined
) => {
  return hasMatchedBusiness(requestState?.matched_business)
    ? Step.OPENING_YEAR
    : Step.STORE_TYPE_S1;
};
